/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { ComponentType, FunctionComponent } from 'react';
import { IDataError } from '@forge/data-connector';
import NoData from '../ErrorComponents/NoData';
import ConnectionError from '../ErrorComponents/ConnectionError';
import './ErrorStatus.scss';
import { apiNoContentStatusCode } from '../Utils/Constants';

export interface IErrorStatusProps {
  error: IDataError;
  title?: string;
  noDataMessage: string;
  connectionErrorMessage: string;
  onRefresh?: () => Promise<void>;
  statusCode?: number | null;
  hideNoData?: boolean;
}

const withErrorStatus = <P extends Object>(
  WrappedComponent: ComponentType<P>
): FunctionComponent<P & IErrorStatusProps> =>
  React.memo(
    ({
      error,
      title,
      noDataMessage,
      connectionErrorMessage,
      onRefresh,
      statusCode,
      hideNoData,
      ...props
    }: IErrorStatusProps) => {
      const status =
        statusCode ??
        (error ? error.status ?? error.rawResponse?.response?.status : null);
      if (status === apiNoContentStatusCode && !hideNoData) {
        return (
          <div className="noDataDisplay common-error-container">
            <NoData message={noDataMessage} />
          </div>
        );
      }

      if (error && (error.name || error.status || error.rawResponse)) {
        return (
          <div className="errorDisplay common-error-container">
            <ConnectionError
              title={title}
              message={connectionErrorMessage}
              onRefresh={onRefresh}
            />
          </div>
        );
      }

      return (
        <WrappedComponent
          {...(props as P)}
          title={title}
          onRefresh={onRefresh}
          statusCode={statusCode}
        />
      );
    }
  );

export default withErrorStatus;
